var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from 'lodash';
import { TweenMax, Draggable } from 'gsap/all';
import DragAndDropHelper from 'mixins/DragAndDropHelper';
import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import CorrectIcon from 'components/icons/WhiteCheckmark.vue';
import DynamicContent from '../components/activity/DynamicContent.vue';
import HtmlRenderer from 'components/activity/HtmlRenderer.vue';
import NextActivityButton from 'components/activity/NextActivityButton.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';
import WrongIcon from 'components/icons/WhiteX.vue';
import ActivityWrapper from '../components/activity/ActivityWrapper.vue';
import { hasTouchedBoundings } from 'utils/activity-util';
import mixins from 'vue-typed-mixins';
const DRAG_DISTANCE_MIN = 100;
export default mixins(DragAndDropHelper()).extend({
    name: 'DragAndDropFractions',
    components: {
        ActivityStage,
        ActivityWrapper,
        BlinkingCursorText,
        CorrectIcon,
        DynamicContent,
        HtmlRenderer,
        NextActivityButton,
        TextDisplayList,
        WrongIcon
    },
    computed: {
        hasGuessedAllAnswers() {
            var _a;
            return this.correctGuesses.length === ((_a = this.currentRound.dropZones) === null || _a === void 0 ? void 0 : _a.length);
        },
        arrayOfDropZoneLength() {
            var _a;
            return Array.from(Array((_a = this.currentRound.dropZones) === null || _a === void 0 ? void 0 : _a.length).keys()).reverse();
        }
    },
    mounted() {
        window.addEventListener('resize', this.recalibrateAnswersOnResize);
    },
    destroyed() {
        window.removeEventListener('resize', this.recalibrateAnswersOnResize);
    },
    methods: {
        // start activity helper methods
        getActivityState() {
            return this.getActivityStateHelper();
        },
        getKeyboardConfig() {
            return this.getKeyboardConfigHelper();
        },
        loadActivity() {
            return __awaiter(this, void 0, void 0, function* () {
                this.loadActivityHelper();
            });
        },
        loadAudios(activityAudios) {
            return __awaiter(this, void 0, void 0, function* () {
                return this.loadAudiosHelper(activityAudios);
            });
        },
        startActivity() {
            this.setRoundHelper();
            this.$nextTick(() => {
                this.setActivityContainerHeight();
            });
        },
        // end activity helper methods
        // start activity specific methods
        checkIfInCorrectDropZone(guess, shouldBeCorrectGuess, guessAction) {
            const draggableElements = Array.from(document.querySelectorAll(`[id^="draggable-${guess.draggableItem.id}-"]`));
            if (!draggableElements)
                return;
            return draggableElements.map(draggableEl => {
                const dragItemCoordinates = draggableEl.getBoundingClientRect();
                const closestDropZone = this.findClosestDropArea(dragItemCoordinates);
                if (!closestDropZone)
                    return;
                const closestDropZoneId = JSON.parse(closestDropZone.id.split('-')[1]);
                const isACorrectGuess = this.correctGuesses.some(correctGuess => correctGuess.droppableItem.id === closestDropZoneId);
                if ((shouldBeCorrectGuess && isACorrectGuess) ||
                    (!shouldBeCorrectGuess && !isACorrectGuess)) {
                    return guessAction(draggableEl);
                }
            });
        },
        createDraggables() {
            let bounds = null;
            const self = this;
            const { isMobile } = this.userDevice;
            this.currentRound.draggableItems.forEach(dragContent => {
                this.arrayOfDropZoneLength.forEach(x => {
                    let hasHitBoundary = false;
                    const hasBeenGuessedCorrectly = this.isGuessedCorrectly(dragContent);
                    Draggable.create(`#draggable-${dragContent.id}-${x}`, {
                        autoScroll: 1,
                        cursor: hasBeenGuessedCorrectly ? 'auto' : 'grab',
                        bound: document.querySelector('.activity-stage'),
                        onDrag() {
                            if (hasBeenGuessedCorrectly)
                                return;
                            // work-around for iOS not firing touchend on tablet
                            if (!isMobile)
                                return;
                            const { pointerX, pointerY } = this;
                            const hasTouched = hasTouchedBoundings(pointerX, pointerY, bounds);
                            if (hasTouched && !hasHitBoundary) {
                                hasHitBoundary = true;
                                self.handleDrop(this);
                            }
                        },
                        onDragStart() {
                            if (hasBeenGuessedCorrectly)
                                return;
                            TweenMax.set(`#draggable-${dragContent.id}-${x}`, {
                                cursor: 'grabbing'
                            });
                        },
                        onDragEnd() {
                            if (hasBeenGuessedCorrectly)
                                return;
                            hasHitBoundary = false;
                            self.handleDrop(this);
                        }
                    });
                });
            });
        },
        disableElement(el) {
            Draggable.get(el).disable();
        },
        disableDraggableCorrectGuesses() {
            this.correctGuesses.forEach(correctGuess => {
                this.checkIfInCorrectDropZone(correctGuess, true, this.disableElement);
            });
        },
        findClosestDropArea(dragItemCoordinates) {
            const allDroppableAreas = this.getAllDroppableAreas();
            let closestDropZone;
            let distance;
            const { x: dragX, y: dragY } = dragItemCoordinates;
            allDroppableAreas.find((droppableArea, index) => {
                const { x: dropX, y: dropY } = droppableArea.position;
                const xDiff = Math.abs(dragX - dropX);
                const yDiff = Math.abs(dragY - dropY);
                const totalDiff = xDiff + yDiff;
                if (!index)
                    distance = totalDiff;
                if (totalDiff <= distance && totalDiff < DRAG_DISTANCE_MIN)
                    closestDropZone = droppableArea;
            });
            return closestDropZone;
        },
        getDropZonePosition(closestDropZone, draggableId) {
            const originalDraggablePos = document
                .querySelector(`.startingPoint-${draggableId}`)
                .getBoundingClientRect();
            const { position: closestDropZonePos } = closestDropZone;
            const xPos = closestDropZonePos.x - originalDraggablePos.x;
            const yPos = closestDropZonePos.y -
                originalDraggablePos.y -
                (originalDraggablePos.height - closestDropZonePos.height) / 2;
            return { xPos, yPos };
        },
        getDragContentBackground(item, index) {
            const colors = this.checkIfInCorrectDropZone({ draggableItem: item }, true, draggableEl => {
                const isCorrect = this.correctGuesses.some(guess => guess.draggableItem.id === item.id);
                if (draggableEl && isCorrect)
                    return 'white';
                return this.defaultColor;
            });
            return colors === null || colors === void 0 ? void 0 : colors[index];
        },
        getIsCorrect(guessedAnswer) {
            return guessedAnswer.droppableItem.correctItemIds.includes(guessedAnswer.draggableItem.id);
        },
        handleDrop(context) {
            const { target } = context;
            const draggableId = target.id.split('-')[1];
            const draggableIndex = target.id.split('-')[2];
            const draggableItem = this.getAssociatedDraggableItem(draggableId);
            if (!draggableItem)
                return;
            const dragItemCoordinates = target.getBoundingClientRect();
            const closestDropZone = this.findClosestDropArea(dragItemCoordinates);
            if (closestDropZone) {
                const closestDropZoneId = JSON.parse(closestDropZone.id.split('-')[1]);
                this.removeFromGuessed(draggableItem, draggableIndex);
                const alreadyGuessed = [...this.guessed, ...this.correctGuesses].some(item => item.droppableItem.id === closestDropZoneId);
                if (alreadyGuessed) {
                    this.resetDraggable(target);
                    return;
                }
                const draggableItemWithIndex = Object.assign(Object.assign({}, draggableItem), { draggableIndex });
                this.handleMoveToDropZone(target, closestDropZone, draggableItemWithIndex, draggableId);
                return;
            }
            this.resetDraggable(target);
            this.removeFromGuessed(draggableItem, draggableIndex);
        },
        handleMoveToDropZone(target, closestDropZone, draggableItem, draggableId, animationLength = 0.5) {
            const { id: dropZoneElId } = closestDropZone;
            const dropAreaId = typeof dropZoneElId === 'number' ? dropZoneElId : dropZoneElId.split('-')[1];
            const droppableItem = this.getAssociatedDroppableItem(dropAreaId);
            const guessedArr = [...this.guessed].filter(guessed => guessed.droppableItem !== droppableItem);
            this.guessed = _.uniq([...guessedArr, { draggableItem, droppableItem }]);
            const { xPos, yPos } = this.getDropZonePosition(closestDropZone, draggableId);
            this.moveDraggableToDropZone(target, xPos + 2, yPos, animationLength);
        },
        isGuessed(droppableItem) {
            var _a;
            if (!droppableItem)
                return false;
            const droppableItemId = +((_a = droppableItem.split('-')) === null || _a === void 0 ? void 0 : _a[1]);
            return [...this.guessed, ...this.correctGuesses].find(item => item.droppableItem.id === droppableItemId);
        },
        isGuessedCorrectly(droppableItem) {
            if (!droppableItem)
                return false;
            return this.correctGuesses.some(item => item.droppableItem.id === droppableItem.id);
        },
        isGuessedIncorrectly(droppableItem) {
            if (!droppableItem)
                return false;
            const isInWrongGuess = this.wrongGuesses.some(item => item.droppableItem.id === droppableItem.id);
            const isGuessed = this.guessed.some(item => item.droppableItem.id === droppableItem.id);
            const isEmpty = !isGuessed && !this.isGuessedCorrectly(droppableItem) && this.isSubmitted;
            return isInWrongGuess || isEmpty;
        },
        removeFromGuessed(draggableItem, draggableIndex) {
            this.guessed = [...this.guessed]
                .map(guess => {
                if (guess.draggableItem.id === draggableItem.id &&
                    +guess.draggableItem.draggableIndex === +draggableIndex)
                    return;
                return guess;
            })
                .filter((x) => x !== undefined);
        },
        resetWrongPieces() {
            this.wrongGuesses.forEach(wrongGuess => {
                this.checkIfInCorrectDropZone(wrongGuess, false, this.resetDraggable);
            });
        },
        // start accessibility methods
        getFractionAriaLabel(id, position, index) {
            let label = `${position} input for fraction ${index ? index : index + 1} with no guesses.`;
            const guessedItem = this.isGuessed(id);
            if (guessedItem) {
                const isCorrectGuess = this.isGuessedCorrectly(guessedItem.droppableItem);
                label = `${position} input for fraction ${index ? index : index + 1} with ${guessedItem.draggableItem.label} as a ${isCorrectGuess ? 'correct guess' : 'guess'}.`;
            }
            return label;
        },
        blurDraggableAndDroppabelItemsStates() {
            Array.from(document.getElementsByClassName('bankAreaItem')).forEach(node => {
                node.blur();
                node.classList.remove('element-selected');
                node.setAttribute('aria-pressed', 'false');
                node.setAttribute('tabindex', '10');
            });
            Array.from(document.getElementsByClassName('droppableItem')).forEach(node => {
                node.blur();
                node.classList.remove('selectable-answer');
            });
        },
        handleOnDraggableItemEnterEvent(fractionSelector) {
            var _a, _b;
            const fractionItemEl = (_a = document.querySelectorAll(`[data-id="${fractionSelector}"]`)) === null || _a === void 0 ? void 0 : _a[0];
            const isSelected = (_b = fractionItemEl === null || fractionItemEl === void 0 ? void 0 : fractionItemEl.classList) === null || _b === void 0 ? void 0 : _b.contains('element-selected');
            Array.from(document.getElementsByClassName('bankAreaItem')).forEach(node => {
                if (!node.classList.contains('element-selected')) {
                    node.classList.remove('element-selected');
                    node.setAttribute('aria-pressed', 'false');
                    node.setAttribute('tabindex', isSelected ? '10' : '-1');
                }
            });
            this.toggleDraggableItemsStates(!isSelected);
            this.toggleDroppableItemState(fractionItemEl, isSelected);
        },
        handleOnDroppableItemEnterEvent(answerSelector) {
            var _a;
            let selectedBankAreaItemEl;
            let selectedDraggableEl;
            Array.from(document.getElementsByClassName('bankAreaItem')).forEach(node => {
                if (node.classList.contains('element-selected')) {
                    selectedBankAreaItemEl = node;
                }
            });
            const guessedItem = this.isGuessed(answerSelector);
            if (guessedItem) {
                const associatedDraggableItem = guessedItem.draggableItem;
                const draggableEl = document.getElementById(`draggable-${associatedDraggableItem.id}-${+associatedDraggableItem.draggableIndex}`);
                this.resetDraggable(draggableEl);
                this.removeFromGuessed(associatedDraggableItem, +associatedDraggableItem.draggableIndex);
                return;
            }
            if (!selectedBankAreaItemEl)
                return;
            const draggableBankAreaItemsSortFn = (a, b) => {
                const { id: aId } = a;
                const { id: bId } = b;
                return aId < bId ? -1 : aId > bId ? 1 : 0;
            };
            const bankAreaGuessesItemsSortFn = (a, b) => {
                const { draggableItem: { draggableIndex: aDraggableIndex } } = a;
                const { draggableItem: { draggableIndex: bDraggableIndex } } = b;
                return aDraggableIndex > bDraggableIndex ? -1 : aDraggableIndex < bDraggableIndex ? 1 : 0;
            };
            let draggableBankAreaItems = [];
            Array.from(document.getElementsByClassName('draggableItem')).forEach((node) => {
                if (selectedBankAreaItemEl.contains(node)) {
                    draggableBankAreaItems.push(node);
                    selectedDraggableEl = node;
                }
            });
            draggableBankAreaItems.sort(draggableBankAreaItemsSortFn);
            if (this.guessed.length) {
                const draggableBankAreaItemsId = draggableBankAreaItems[0].id.split('-')[1];
                const bankAreaGuesses = this.guessed.filter(({ draggableItem: { id } }) => {
                    return draggableBankAreaItemsId === `${id}`;
                });
                if (bankAreaGuesses.length) {
                    bankAreaGuesses.sort(bankAreaGuessesItemsSortFn);
                    const currentDraggableBankAreaItemIndex = draggableBankAreaItems.findIndex(node => {
                        const draggableBankAreaItemsIndex = node.id.split('-')[2];
                        return draggableBankAreaItemsIndex === bankAreaGuesses[0].draggableItem.draggableIndex;
                    });
                    const removedGuess = this.guessed.find(({ droppableItem: { id } }) => `${id}` === answerSelector.split('-')[1]);
                    if (removedGuess) {
                        selectedDraggableEl = draggableBankAreaItems.find(item => {
                            const [_, draggableItemId, draggableItemIndex] = item.id.split('-');
                            const { draggableItem: { draggableIndex, id } } = removedGuess;
                            return draggableIndex === draggableItemIndex && `${id}` === draggableItemId;
                        });
                    }
                    if (!removedGuess && draggableBankAreaItems[currentDraggableBankAreaItemIndex + 1]) {
                        selectedDraggableEl = draggableBankAreaItems[currentDraggableBankAreaItemIndex + 1];
                    }
                }
            }
            const [_, draggableItemId, draggableItemIndex] = selectedDraggableEl.id.split('-');
            const associatedDraggableItem = this.getAssociatedDraggableItem(draggableItemId);
            if (!associatedDraggableItem)
                return;
            const closestDropZone = (_a = this.getAllDroppableAreas()) === null || _a === void 0 ? void 0 : _a.find(({ id }) => id === answerSelector);
            if (closestDropZone) {
                this.removeFromGuessed(associatedDraggableItem, draggableItemIndex);
                this.handleMoveToDropZone(selectedDraggableEl, closestDropZone, Object.assign(Object.assign({}, associatedDraggableItem), { draggableIndex: draggableItemIndex }), draggableItemId);
                this.blurDraggableAndDroppabelItemsStates();
            }
        },
        recalibrateAnswersOnResize() {
            if (!this.guessed.length)
                return;
            this.guessed.forEach(({ draggableItem, droppableItem }) => {
                var _a;
                const closestDropZone = (_a = this.getAllDroppableAreas()) === null || _a === void 0 ? void 0 : _a.find(({ id }) => id === `dropArea-${droppableItem.id}`);
                const target = document.getElementById(`draggable-${draggableItem.id}-${draggableItem.draggableIndex}`);
                this.handleMoveToDropZone(target, closestDropZone, draggableItem, draggableItem.id, 0);
            });
        },
        toggleDraggableItemsStates(isSelectable) {
            Array.from(document.getElementsByClassName('droppableItem')).forEach(node => {
                if (isSelectable) {
                    node.classList.add('selectable-answer');
                    node.setAttribute('tabindex', '10');
                }
                else {
                    node.classList.remove('selectable-answer');
                    node.setAttribute('tabindex', '-1');
                }
            });
        },
        toggleDroppableItemState(item, isSelected) {
            if (isSelected) {
                item === null || item === void 0 ? void 0 : item.setAttribute('aria-pressed', 'false');
                item === null || item === void 0 ? void 0 : item.classList.remove('element-selected');
                item === null || item === void 0 ? void 0 : item.setAttribute('tabindex', '10');
            }
            else {
                item === null || item === void 0 ? void 0 : item.setAttribute('aria-pressed', 'true');
                item === null || item === void 0 ? void 0 : item.classList.add('element-selected');
                item === null || item === void 0 ? void 0 : item.setAttribute('tabindex', '10');
            }
        }
        // end accessibility methods
    },
    watch: {
        incorrectGuesses() {
            this.blurDraggableAndDroppabelItemsStates();
        },
        wrongGuesses() {
            this.blurDraggableAndDroppabelItemsStates();
        }
    }
});
